import { useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Space, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import CustomerService from 'services/customer';
import { useAddressPicker } from '.';

const service = new CustomerService();

const CustomerInfo = ({ open, onClose }) => {
    const { address, onChange, customer_id } = useAddressPicker();
    const [shippingAddresses, setShippingAddresses] = useState([]);
    const [selectedAddressIndex, setSelectedAddressIndex] = useState(undefined);
    const [saving, setSaving] = useState(false);

    const [form] = Form.useForm();

    useEffect(() => {
        form.setFieldsValue(address);
    }, [address, form]);

    useEffect(() => {
        if (!open || !customer_id) {
            setShippingAddresses([]);
            setSelectedAddressIndex(undefined);
            return;
        }
        loadCustomerAddresses();
    }, [open, customer_id]);

    const normalizeShippingAddress = (list = [], customer = {}) => {
        let addresses = (list ?? []).map((x, index) => ({
            key: `${index}`,
            full_name: x?.full_name ?? customer?.full_name ?? "",
            phone_number: x?.phone_number ?? customer?.phone ?? "",
            address: x?.address ?? "",
            note: x?.note ?? "",
            is_default: !!x?.is_default,
        }));

        if (addresses.length === 0 && customer?.address) {
            addresses = [{
                key: "0",
                full_name: customer?.full_name ?? "",
                phone_number: customer?.phone ?? "",
                address: customer?.address ?? "",
                note: "",
                is_default: true,
            }];
        }

        if (addresses.length > 0 && !addresses.some(x => x.is_default)) {
            addresses[0].is_default = true;
        }

        return addresses;
    };

    const loadCustomerAddresses = async () => {
        const cus = await service.getByID(customer_id);
        if (!cus) {
            setShippingAddresses([]);
            setSelectedAddressIndex(undefined);
            return;
        }

        const addresses = normalizeShippingAddress(cus.shipping_address, cus);
        setShippingAddresses(addresses);

        const matched = addresses.findIndex(x => x.full_name == address?.full_name && x.phone_number == address?.phone_number && x.address == address?.address);
        if (matched >= 0) {
            setSelectedAddressIndex(`${matched}`);
            return;
        }

        const defaultAddress = addresses.find(x => x.is_default) ?? addresses[0];
        const defaultIndex = addresses.findIndex(x => x.key == defaultAddress?.key);
        if (defaultAddress) {
            setSelectedAddressIndex(`${defaultIndex}`);
        }
    };

    const onSelectExistingAddress = (value) => {
        setSelectedAddressIndex(value);
        const selected = shippingAddresses[Number(value)];
        if (!selected) {
            return;
        }
        form.setFieldsValue({
            phone_number: selected.phone_number,
            full_name: selected.full_name,
            address: selected.address,
        });
    };

    const onAddNewAddress = () => {
        setSelectedAddressIndex(undefined);
        form.setFieldsValue({
            phone_number: '',
            full_name: '',
            address: '',
        });
    };

    const toShippingAddressPayload = (values = {}) => ({
        full_name: String(values?.full_name || '').trim(),
        phone_number: String(values?.phone_number || '').trim(),
        address: String(values?.address || '').trim(),
        note: '',
    });

    const saveShippingAddress = async (values = {}) => {
        if (!customer_id) {
            return;
        }

        const normalizedCurrent = shippingAddresses.map((item) => ({
            full_name: String(item?.full_name || ''),
            phone_number: String(item?.phone_number || ''),
            address: String(item?.address || ''),
            note: String(item?.note || ''),
            is_default: Boolean(item?.is_default),
        }));

        const nextAddress = {
            ...toShippingAddressPayload(values),
            is_default: normalizedCurrent.length === 0,
        };

        const selectedIndex = Number(selectedAddressIndex);
        const shouldUpdateExisting = Number.isInteger(selectedIndex) && selectedIndex >= 0 && selectedIndex < normalizedCurrent.length;

        let nextList = normalizedCurrent;
        if (shouldUpdateExisting) {
            nextList = normalizedCurrent.map((item, index) => {
                if (index !== selectedIndex) {
                    return item;
                }
                return {
                    ...item,
                    ...nextAddress,
                    is_default: item.is_default,
                };
            });
        } else {
            nextList = [...normalizedCurrent, nextAddress];
        }

        if (nextList.length > 0 && !nextList.some((item) => item.is_default)) {
            nextList[0].is_default = true;
        }

        await service.update({
            id: customer_id,
            shipping_address: nextList,
        });

        const newestIndex = shouldUpdateExisting ? selectedIndex : nextList.length - 1;
        await loadCustomerAddresses();
        setSelectedAddressIndex(`${newestIndex}`);
    };

    const onCancel = () => {
        onClose?.();
    };

    const btnSaveClick = () => {
        form.submit();
    };

    const onFinish = async (values) => {
        try {
            setSaving(true);
            await saveShippingAddress(values);

            onChange(values);
            message.success('Đã lưu địa chỉ giao hàng');
            onClose?.();
        } catch (err) {
            message.error(String(err?.message || err || 'Không thể lưu địa chỉ giao hàng'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Modal
                title="Sửa địa chỉ"
                closable={{ 'aria-label': 'Custom Close Button' }}
                open={open}
                onCancel={onCancel}
                footer={<Space>
                    <Button onClick={onCancel}>Đóng</Button>
                    <Button onClick={btnSaveClick} type='primary' loading={saving}>Lưu</Button>
                </Space>}
            >
                <Form layout='vertical' form={form} onFinish={onFinish}>

                    {shippingAddresses.length > 0 && (
                        <Form.Item label="Địa chỉ có sẵn">
                            <Space direction='vertical' style={{ width: "100%" }}>
                                {shippingAddresses.map((x, index) => (
                                    <div key={x.key ?? index} style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: 8 }}>
                                        <Space style={{ width: "100%", justifyContent: "space-between" }} align='start'>
                                            <div>
                                                <div><b>{x.full_name}</b> - {x.phone_number}</div>
                                                <div>{x.address}</div>
                                                {x.is_default ? <div style={{ color: "#52c41a" }}>Mặc định</div> : null}
                                            </div>
                                            <Button
                                                type='link'
                                                onClick={() => onSelectExistingAddress(`${index}`)}
                                                disabled={selectedAddressIndex === `${index}`}
                                            >
                                                {selectedAddressIndex === `${index}` ? "Đã chọn" : "Chọn"}
                                            </Button>
                                        </Space>
                                    </div>
                                ))}
                                <Button icon={<PlusOutlined />} onClick={onAddNewAddress}>
                                    Thêm địa chỉ mới
                                </Button>
                            </Space>
                        </Form.Item>
                    )}

                    <Form.Item
                        label="Số điện thoại"
                        name={"phone_number"}
                        rules={[
                            {
                                required: true,
                                pattern: /^[0-9]{10}$/,
                                message: "Số điện thoại không đúng định dạng",
                            },
                        ]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Họ  và tên"
                        name={"full_name"}

                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Địa chỉ"
                        name={"address"}

                    >
                        <Input />
                    </Form.Item>

                </Form>
            </Modal>
        </>
    );
};
export default CustomerInfo;